/**
 * Pure maths for Study page images (study-capture-analyse-prd.md §3.2, §4.2). No browser APIs
 * here, so every rule is unit-tested: page size limits, rotation, the photo quality hints and
 * the mapping from a normalised analysis region to pixels and to a Study crop.
 */

import type { StudyRegion } from './studyAnalysisApi';

/** Long edge of a page PNG; matches the server (1600 px per side). */
export const PAGE_MAX_EDGE = 1600;
/** Short edge under this size gets the non-blocking "looks blurry" hint (PRD §3.2). */
export const BLURRY_SHORT_EDGE = 800;
/**
 * Long edge of the grayscale copy used for the sharpness check (study-capture-fixes-prd.md
 * F-12). Downscaling first keeps the check fast and makes the threshold independent of the
 * camera resolution.
 */
export const SHARPNESS_SAMPLE_EDGE = 512;
/**
 * Laplacian variance (0–255 grayscale, 3×3 kernel) under this value counts as blurry.
 * PROVISIONAL: the PRD requires calibration with 20 team photos (sharp and blurry pages, no
 * learner material). Until that set exists, 100 is the common starting point for printed text;
 * it only shows a hint and never blocks an upload.
 */
export const BLURRY_LAPLACIAN_VARIANCE = 100;
/** Mean luminance (0–255) under this value counts as an obviously dark photo. */
export const DARK_MEAN_LUMINANCE = 60;
/** A shrink step when a PNG is over the byte limit. */
export const PNG_SHRINK_FACTOR = 0.8;
/** The server crop schema needs at least 3% of the page in each direction. */
export const MIN_CROP_FRACTION = 0.03;

export type PageRotation = 0 | 90 | 180 | 270;

export interface Size {
  width: number;
  height: number;
}

/** Scales a size down (never up) so its long edge is at most `maxEdge`. */
export function fitWithin(size: Size, maxEdge = PAGE_MAX_EDGE): Size {
  const longEdge = Math.max(size.width, size.height);
  if (!(longEdge > 0)) return { width: 1, height: 1 };
  const scale = Math.min(1, maxEdge / longEdge);
  return {
    width: Math.max(1, Math.min(maxEdge, Math.round(size.width * scale))),
    height: Math.max(1, Math.min(maxEdge, Math.round(size.height * scale))),
  };
}

/** The output size after a quarter-turn rotation. */
export function rotatedSize(size: Size, rotation: PageRotation): Size {
  return rotation === 90 || rotation === 270
    ? { width: size.height, height: size.width }
    : { ...size };
}

/** The next clockwise quarter turn. */
export function nextRotation(rotation: PageRotation): PageRotation {
  return ((rotation + 90) % 360) as PageRotation;
}

/**
 * A smaller size to try when the encoded PNG is over the byte limit. Returns null when the
 * page cannot get smaller in a useful way.
 */
export function shrinkForBytes(size: Size, bytes: number, limit: number): Size | null {
  if (bytes <= limit) return size;
  // PNG size grows roughly with the pixel count, so scale each side by the square root.
  const ratio = Math.min(PNG_SHRINK_FACTOR, Math.sqrt(limit / bytes) * 0.95);
  const next = {
    width: Math.max(1, Math.floor(size.width * ratio)),
    height: Math.max(1, Math.floor(size.height * ratio)),
  };
  if (Math.max(next.width, next.height) < 200) return null;
  return next;
}

/** The pdf.js viewport scale that renders a page with its long edge at `maxEdge`. */
export function pdfRenderScale(base: Size, maxEdge = PAGE_MAX_EDGE): number {
  const longEdge = Math.max(base.width, base.height);
  if (!(longEdge > 0)) return 1;
  return Math.min(3, maxEdge / longEdge);
}

/** The sample size for the sharpness check: long edge at most `maxEdge`, never upscaled. */
export function sharpnessSampleSize(size: Size, maxEdge = SHARPNESS_SAMPLE_EDGE): Size {
  return fitWithin(size, maxEdge);
}

/** Rec. 709 luma (0–255) of RGBA pixels, one value per pixel. */
export function toGrayscale(rgba: ArrayLike<number>): Float32Array {
  const pixels = Math.floor(rgba.length / 4);
  const gray = new Float32Array(pixels);
  for (let pixel = 0; pixel < pixels; pixel += 1) {
    const offset = pixel * 4;
    gray[pixel] = 0.2126 * rgba[offset] + 0.7152 * rgba[offset + 1] + 0.0722 * rgba[offset + 2];
  }
  return gray;
}

/**
 * Variance of the 3×3 Laplacian (4-neighbour kernel) over the inner pixels of a grayscale image.
 * Sharp edges give large positive and negative responses (high variance); a blurred page gives
 * small responses (low variance). Returns null when the image is too small to measure.
 */
export function laplacianVariance(
  gray: ArrayLike<number>,
  width: number,
  height: number,
): number | null {
  if (width < 3 || height < 3 || gray.length < width * height) return null;
  let sum = 0;
  let sumOfSquares = 0;
  let count = 0;
  for (let y = 1; y < height - 1; y += 1) {
    for (let x = 1; x < width - 1; x += 1) {
      const index = y * width + x;
      const response =
        gray[index - width] + gray[index + width] + gray[index - 1] + gray[index + 1] -
        4 * gray[index];
      sum += response;
      sumOfSquares += response * response;
      count += 1;
    }
  }
  const mean = sum / count;
  return sumOfSquares / count - mean * mean;
}

/** True when a measured sharpness is under the blur threshold. */
export function looksSoft(sharpness: number | null): boolean {
  return sharpness !== null && sharpness < BLURRY_LAPLACIAN_VARIANCE;
}

/**
 * True when the photo may be hard to read: it is small, or its sharpness (Laplacian variance of
 * the downscaled grayscale copy) is under the threshold. Null sharpness means "not measured".
 */
export function looksBlurry(original: Size, sharpness: number | null = null): boolean {
  return Math.min(original.width, original.height) < BLURRY_SHORT_EDGE || looksSoft(sharpness);
}

/** Mean luminance of RGBA pixels, sampled every `step` pixels. */
export function meanLuminance(rgba: ArrayLike<number>, step = 1): number {
  const pixels = Math.floor(rgba.length / 4);
  if (pixels === 0) return 255;
  const stride = Math.max(1, Math.floor(step));
  let total = 0;
  let count = 0;
  for (let pixel = 0; pixel < pixels; pixel += stride) {
    const offset = pixel * 4;
    total += 0.2126 * rgba[offset] + 0.7152 * rgba[offset + 1] + 0.0722 * rgba[offset + 2];
    count += 1;
  }
  return total / count;
}

export function looksDark(rgba: ArrayLike<number>, step = 1): boolean {
  return meanLuminance(rgba, step) < DARK_MEAN_LUMINANCE;
}

function clamp01(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.min(1, Math.max(0, value));
}

/** A region clamped inside the page; null when nothing of it is on the page. */
export function clampRegion(region: StudyRegion): StudyRegion | null {
  const x = clamp01(region.x);
  const y = clamp01(region.y);
  const right = clamp01(region.x + region.w);
  const bottom = clamp01(region.y + region.h);
  if (right <= x || bottom <= y) return null;
  return { x, y, w: right - x, h: bottom - y };
}

export interface PixelRect {
  sx: number;
  sy: number;
  sw: number;
  sh: number;
}

/** Maps a normalised region to whole pixels inside an image of `size`. */
export function regionToPixels(region: StudyRegion, size: Size): PixelRect | null {
  const clamped = clampRegion(region);
  if (!clamped) return null;
  const sx = Math.min(size.width - 1, Math.max(0, Math.floor(clamped.x * size.width)));
  const sy = Math.min(size.height - 1, Math.max(0, Math.floor(clamped.y * size.height)));
  const right = Math.min(size.width, Math.ceil((clamped.x + clamped.w) * size.width));
  const bottom = Math.min(size.height, Math.ceil((clamped.y + clamped.h) * size.height));
  return { sx, sy, sw: Math.max(1, right - sx), sh: Math.max(1, bottom - sy) };
}

/**
 * The Study source-region crop for a question region. A region smaller than the server minimum
 * grows around its centre, and the crop always stays inside the page.
 */
export function regionToCrop(region: StudyRegion): {
  x: number;
  y: number;
  width: number;
  height: number;
} | null {
  const clamped = clampRegion(region);
  if (!clamped) return null;
  const grow = (start: number, length: number) => {
    if (length >= MIN_CROP_FRACTION) return { start, length };
    const centre = start + length / 2;
    const grownStart = Math.min(1 - MIN_CROP_FRACTION, Math.max(0, centre - MIN_CROP_FRACTION / 2));
    return { start: grownStart, length: MIN_CROP_FRACTION };
  };
  const horizontal = grow(clamped.x, clamped.w);
  const vertical = grow(clamped.y, clamped.h);
  const round = (value: number) => Math.round(value * 1_000_000) / 1_000_000;
  const x = round(horizontal.start);
  const y = round(vertical.start);
  return {
    x,
    y,
    width: round(Math.min(horizontal.length, 1 - x)),
    height: round(Math.min(vertical.length, 1 - y)),
  };
}

/** Converts a crop drawn with the existing focus overlay into an analysis region. */
export function cropToRegion(crop: {
  x: number;
  y: number;
  width: number;
  height: number;
}): StudyRegion | null {
  return clampRegion({ x: crop.x, y: crop.y, w: crop.width, h: crop.height });
}
